import React, { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import api from '../lib/api';
import toast from 'react-hot-toast';
import LoadingSpinner from '@shared/ui/LoadingSpinner';

const CampaignShortUrlPage: React.FC = () => {
  const { shortId } = useParams<{ shortId: string }>();
  const navigate = useNavigate();

  const { data, isLoading, error } = useQuery(
    ['campaign-short-url', shortId],
    async () => {
      const response = await api.get(`/campaigns/short/${shortId}`);
      return response.data;
    },
    {
      enabled: !!shortId,
      retry: false,
      refetchOnWindowFocus: false,
    }
  );

  useEffect(() => {
    if (data?.success && data?.data?.redirect_url) {
      // Extract the path from the redirect URL to navigate within the app
      try {
        const url = new URL(data.data.redirect_url);
        const path = url.pathname + url.search;
        navigate(path, { replace: true });
      } catch {
        // Fallback: navigate to the full URL
        window.location.href = data.data.redirect_url;
      }
    } else if (error) {
      toast.error('Campaign link not found or no longer active');
      navigate('/', { replace: true });
    }
  }, [data, error, navigate]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background dark:bg-background-dark-primary">
        <div className="text-center">
          <LoadingSpinner size="xl" className="mx-auto mb-4" />
          <h2 className="text-lg font-semibold text-neutral-900 dark:text-white mb-2">
            Loading Campaign...
          </h2>
          <p className="text-neutral-600 dark:text-text-dark-tertiary">
            Redirecting you to the campaign page
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background dark:bg-background-dark-primary">
        <div className="text-center">
          <div className="text-red-500 text-6xl mb-4">⚠️</div>
          <h2 className="text-2xl font-bold text-neutral-900 dark:text-white mb-4">
            Campaign Not Found
          </h2>
          <p className="text-neutral-600 dark:text-text-dark-tertiary mb-6">
            This campaign link is no longer active or doesn&apos;t exist.
          </p>
          <button
            onClick={() => navigate('/')}
            className="px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            Go to Homepage
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background dark:bg-background-dark-primary">
      <div className="text-center">
        <LoadingSpinner size="xl" className="mx-auto mb-4" />
        <h2 className="text-lg font-semibold text-neutral-900 dark:text-white mb-2">
          Redirecting...
        </h2>
      </div>
    </div>
  );
};

export default CampaignShortUrlPage;
